import React, { useEffect, useState } from "react";
import { withRouter } from "react-router";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { withStyles } from "@material-ui/styles";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { TableCell } from "@material-ui/core";

import { withTranslation } from "../../../core/utils";

import { deleteData, postData, putData } from "../../../core/fetchService";
import { DIALOG_USER_STATE } from "../../../core/constants";

import {
  getProjectId,
  fetchBackends,
  getBackends,
  setBackends,
} from "../../../features/settings";

import IconButton from "../../components/material-ui/IconButton";
import EnhancedTable from "../../components/projectTable";
import useConst from "../../components/helpers/useConst";
import ConfirmDialog from "../../components/confirmDialog";

import EditDialog from "./EditDialog";

const styles = (theme) => ({
  dialogField: {
    margin: "8px 0",
  },
  selectType: {
    minWidth: "100%",
    margin: "5px 0",
    "& > label": {
      transform: "translate(14px, 12px) scale(1)",
    },
  },
  selectField: {
    maxHeight: "40px",
  },
  _fieldset: {
    margin: "10px",
    padding: "20px",
    borderRadius: 5,
    borderWidth: 1,
  },
  legend: {
    color: "#757575",
  },
  inlineFloatRightIcon: {
    float: "right",
    marginLeft: "20px",
    marginTop: "-25px",
    marginRight: "-15px",
    visibility: "hidden",
    "*:hover > &": {
      visibility: "visible",
    },
  },
  checkSampleFirstRow: {
    display: "flex",
    flex: 1,
    flexDirection: "row",
    margin: "0 10px",
  },
  dasboardWrapper: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 20,
  },
  dashboardButtonGroup: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    gap: "5px",
  },
  dashboardTableWrapper: {
    maxWidth: "100%",
    overflowY: "auto",
  }
});

const Backends = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const backends = useSelector(getBackends);
  const projectId = useSelector(getProjectId);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);
  const [isClone, setIsClone] = useState(false);
  const [rowId, setRowId] = useState(null);

  useEffect(() => {
    dispatch(fetchBackends());
  }, [projectId]);

  const headCells = useConst(() => [
    {
      _id: "name",
      width: "20%",
      label: t("common.name"),
      textSearch: true,
    },
    {
      _id: "type",
      width: "10%",
      label: t("common.type"),
      filterOn: true,
    },
    {
      _id: "description",
      width: "auto",
      label: t("common.description"),
      textSearch: true,
    },
    {
      _id: "url",
      width: "auto",
      label: t("backends.url"),
      textSearch: true,
    },
    {
      _id: "updatedAt",
      width: "10%",
      label: t("common.date"),
      dateTime: true,
    },
  ]);

  const handleNewRowModalOpen = () => {
    setIsClone(false);
    setIsModalOpen(true);
    setRowId(null);
  };

  const handleRowUpdate = () => dispatch(fetchBackends());

  const handleRowEditModalOpen = (id) => {
    setIsClone(false);
    setIsModalOpen(true);
    setRowId(id);
  };

  const handleRowCloneModalOpen = (id) => {
    setIsClone(true);
    setIsModalOpen(true);
    setRowId(id);
  };

  const handleRowEdit = (backend, onClose) => {
    putData(
      `/api/backend/${rowId}`,
      { backend },
      dispatch,
      (data) => {
        dispatch(setBackends(backends.map(b => b._id == data.backend._id ? data.backend : b)));
        onClose();
      }
    );
  };

  const handleNewBackendSave = (backend, onClose) => {
    postData(
      `/api/backend${isClone ? `/copy/${rowId}` : ""}`,
      {
        backend: {
          ...backend,
          project: projectId,
        },
      },
      dispatch,
      (data) => {
        dispatch(setBackends([...backends, data.backend]));
        onClose();
      }
    );
  };

  const handleConfirmDeleteModalOpen = (id) => {
    setRowId(id);
    setIsShowConfirmModal(true);
  };

  const handleConfirmModalClose = (status) => {
    if (status === DIALOG_USER_STATE.AGREE) {
      deleteData(`/api/backend/${rowId}`, dispatch, () => {
        dispatch(setBackends(backends.filter(b => b._id !== rowId)));
      });
    }
    setIsShowConfirmModal(false);
  };

  return backends ? (
    <>
      {isModalOpen && (
        <EditDialog
          rowId={rowId}
          isClone={isClone}
          classes={props.classes}
          backendList={backends}
          onSave={rowId && !isClone ? handleRowEdit : handleNewBackendSave}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      {isShowConfirmModal && (
        <ConfirmDialog
          closeModal={handleConfirmModalClose}
          btnNameAgree={t("common.ok")}
          btnNameDisagree={t("common.cancel")}
          title={t("backends.delete_backend")}
          content={`${t("backends.delete_backend")}: "${
            backends.find((item) => item._id == rowId).name
          }"?`}
        />
      )}
      <EnhancedTable
        id="backends"
        toolBarName={t("backends.title")}
        useLocationSearch
        headCells={headCells}
        rows={backends}
        newRowTitle={t("backends.add_backend")}
        handleClickNewRow={handleNewRowModalOpen}
        handleClickUpdateRow={handleRowUpdate}
        checkBoxTableCell={(id, name, index) => (
          <TableCell padding="default">{index + 1}</TableCell>
        )}
        customBtns={(name, id) => {
          const ICONS = [
            ["edit", handleRowEditModalOpen, EditIcon],
            ["clone", handleRowCloneModalOpen, FileCopyIcon],
            ["delete", handleConfirmDeleteModalOpen, DeleteIcon],
          ];

          return (
            <div style={{ width: "25%", display: "flex" }}>
              {ICONS.map((i) => (
                <IconButton
                  key={i[0]}
                  onClick={() => i[1](id, name)}
                  title={t("common." + i[0])}
                  Icon={i[2]}
                />
              ))}
            </div>
          );
        }}
      />
    </>
  ) : null;
};

export default withRouter(withStyles(styles)(withTranslation()(Backends)));
